/**
 * Staff Requisition Reports
 * Displays a filterable, paginated requisition register with Excel export.
 *
 * Status/Type are filtered server-side (via hrRequisitionApi.report). Year and Department
 * filters, and pagination, are applied client-side against a wide-window fetch — same pattern
 * used by Procurement Reports and Inventory Reports, since the backend report endpoint doesn't
 * accept year/department query params.
 */
import { useEffect, useMemo, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { hrRequisitionApi } from "../api";
import { downloadXls } from "../utils/export";
import { fetchDepartmentOptions } from "#/features/employees/api";
import { useAsyncData } from "#/features/inventory/hooks";
import type { HrRequisitionLine, HrRequisitionStatus, RequisitionType } from "../types";

const STATUS_OPTS: { label: string; value: HrRequisitionStatus | "" }[] = [
  { label: "All Statuses",    value: ""                },
  { label: "Draft",           value: "DRAFT"           },
  { label: "Pending Approval",value: "PENDING_APPROVAL"},
  { label: "Approved",        value: "APPROVED"        },
  { label: "Fulfilled",       value: "FULFILLED"       },
  { label: "Partial",         value: "PARTIAL"         },
  { label: "Rejected",        value: "REJECTED"        },
  { label: "Cancelled",       value: "CANCELLED"       },
];

// All five historical types stay filterable here even though only Goods/Asset Request are
// creatable going forward (2026-07-09) — Service/Works/General records may still exist and
// should remain reportable.
const TYPE_OPTS: { label: string; value: RequisitionType | "" }[] = [
  { label: "All Types",       value: ""            },
  { label: "Goods",           value: "GOODS"        },
  { label: "Asset Request",   value: "ASSET_REQUEST"},
  { label: "Service",         value: "SERVICE"      },
  { label: "Works",           value: "WORKS"        },
  { label: "General",         value: "GENERAL"      },
];

function statusBadge(s: string) {
  const cls: Record<string, string> = {
    DRAFT:            "bg-slate-100 text-slate-600",
    PENDING_APPROVAL: "bg-blue-100 text-blue-700",
    APPROVED:         "bg-emerald-100 text-emerald-700",
    FULFILLED:        "bg-purple-100 text-purple-700",
    PARTIAL:          "bg-sky-100 text-sky-700",
    REJECTED:         "bg-red-100 text-red-600",
    CANCELLED:        "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls[s] ?? "bg-slate-100 text-slate-500"}`}>
      {s.replace(/_/g, " ")}
    </span>
  );
}

function fmtDate(s?: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
}

// The report endpoint only returns `lineCount`, not full line descriptions — fetch those
// separately (see linesById below) and format them here, shared between the on-screen table
// and the Excel export. Shows what was issued (vs. requested) whenever they differ, since
// "what did we actually hand out" is the point of a report, not just what was asked for.
function describeLine(l: HrRequisitionLine): string {
  const qtyPart = l.quantity ? ` x${l.quantity}` : "";
  const issuedPart =
    l.issuedQuantity != null && l.issuedQuantity !== l.quantity
      ? ` (${l.issuedQuantity} issued)`
      : "";
  return `${l.description}${qtyPart}${issuedPart}`;
}

function itemsSummaryText(lines?: HrRequisitionLine[] | null): string {
  if (!lines || lines.length === 0) return "—";
  return lines.map(describeLine).join("; ");
}

function ItemsCell({ lines, pending }: { lines?: HrRequisitionLine[] | null; pending?: boolean }) {
  if (!lines || lines.length === 0) {
    return <span className="text-slate-300">{pending ? "…" : "—"}</span>;
  }
  const first = lines[0];
  const extra = lines.length - 1;
  return (
    <span>
      {describeLine(first)}
      {extra > 0 && <span className="ml-1.5 text-slate-400">+{extra} more</span>}
    </span>
  );
}

export default function RequisitionReportsPage() {
  const [status, setStatus] = useState<HrRequisitionStatus | "">("");
  const [type, setType]     = useState<RequisitionType | "">("");
  const [year, setYear]     = useState<number | "ALL">("ALL");
  const [departmentId, setDepartmentId] = useState<number | "">("");
  const [page, setPage]     = useState(0);
  const [size, setSize]     = useState(20);
  const [exporting, setExporting] = useState(false);

  // Wide window — status/type filters happen server-side, year/department/pagination happen
  // client-side below, same as Procurement Reports and Inventory Reports.
  const { data, loading, error } = useAsyncData(
    () => hrRequisitionApi.report({ page: 0, size: 500, status: status || undefined, type: type || undefined }),
    [status, type],
  );

  const { data: departments } = useAsyncData(() => fetchDepartmentOptions(), []);

  const rows = useMemo(() => data?.content ?? [], [data]);

  // Years actually present in the loaded set — current year always included so a brand-new
  // year with no data yet still shows up and defaults sensibly.
  const years = useMemo(() => {
    const set = new Set<number>([new Date().getFullYear()]);
    for (const r of rows) {
      if (r.submittedAt) set.add(new Date(r.submittedAt).getFullYear());
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (year !== "ALL") {
        if (!r.submittedAt || new Date(r.submittedAt).getFullYear() !== year) return false;
      }
      if (departmentId && r.departmentName) {
        const dept = (departments ?? []).find((d) => d.id === departmentId);
        if (dept && r.departmentName !== dept.name) return false;
      }
      return true;
    });
  }, [rows, year, departmentId, departments]);

  // Reset to page 1 whenever a filter changes underneath the current page.
  useEffect(() => { setPage(0); }, [status, type, year, departmentId]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / size));
  const pageItems = filtered.slice(page * size, page * size + size);

  // The report endpoint doesn't return full lines, only lineCount — fetch item descriptions
  // per row from the detail endpoint, but only for rows actually on screen (not all 500 in the
  // wide window), and cache by id so paging back doesn't refetch. Export does a fuller sweep
  // separately, below, since it needs every filtered row rather than just the visible page.
  const [linesById, setLinesById] = useState<Record<number, HrRequisitionLine[]>>({});

  useEffect(() => {
    const toFetch = pageItems.filter((r) => !linesById[r.id]);
    if (toFetch.length === 0) return;
    let cancelled = false;
    Promise.all(
      toFetch.map((r) =>
        hrRequisitionApi.get(r.id).then(
          (full): [number, HrRequisitionLine[]] => [r.id, full.lines ?? []],
          (): [number, HrRequisitionLine[]] => [r.id, []],
        ),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      setLinesById((prev) => {
        const next = { ...prev };
        for (const [id, lines] of pairs) next[id] = lines;
        return next;
      });
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageItems]);

  async function doExport() {
    setExporting(true);
    try {
      // Export covers every filtered row, not just the visible page — fetch whatever isn't
      // already cached from browsing before building the sheet.
      const missing = filtered.filter((r) => !linesById[r.id]);
      let allLines = linesById;
      if (missing.length > 0) {
        const pairs = await Promise.all(
          missing.map((r) =>
            hrRequisitionApi.get(r.id).then(
              (full): [number, HrRequisitionLine[]] => [r.id, full.lines ?? []],
              (): [number, HrRequisitionLine[]] => [r.id, []],
            ),
          ),
        );
        allLines = { ...linesById, ...Object.fromEntries(pairs) };
        setLinesById(allLines);
      }
      await downloadXls(
        `staff-requisitions-${new Date().toISOString().slice(0, 10)}`,
        "Staff Requisitions Report",
        ["Serial No", "Type", "Requester", "Department", "Submitted", "Lines", "Item(s) / Issued", "Status"],
        filtered.map((r) => [
          r.serialNo ?? `#${r.id}`,
          (r.type ?? r.requisitionType ?? "—").replace(/_/g, " "),
          r.requesterName ?? "—",
          r.departmentName ?? "—",
          fmtDate(r.submittedAt),
          r.lineCount ?? "—",
          itemsSummaryText(allLines[r.id]),
          r.status.replace(/_/g, " "),
        ]),
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Staff Requisitions</p>
        <h1 className="text-xl font-bold text-slate-900">Reports</h1>
      </div>

      {/* Filters + Export */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={status}
          onChange={e => setStatus(e.target.value as HrRequisitionStatus | "")}
          className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select
          value={type}
          onChange={e => setType(e.target.value as RequisitionType | "")}
          className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          {TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select
          value={year}
          onChange={e => setYear(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}
          className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          <option value="ALL">All Years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select
          value={departmentId}
          onChange={e => setDepartmentId(e.target.value ? Number(e.target.value) : "")}
          className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          <option value="">All Departments</option>
          {(departments ?? []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>

        <button
          onClick={doExport}
          disabled={exporting || filtered.length === 0}
          className="ml-auto flex items-center gap-1.5 rounded-xl border border-emerald-300 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
        >
          <FileSpreadsheet className="h-4 w-4" />
          {exporting ? "Exporting…" : "Export Excel"}
        </button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
        {loading && <div className="px-5 py-8 text-center text-sm text-slate-400">Loading report…</div>}
        {error   && <div className="px-5 py-4 text-sm text-red-600">{error}</div>}

        {!loading && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Serial No</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Requester</th>
                    <th className="px-4 py-3 text-left">Department</th>
                    <th className="px-4 py-3 text-left">Submitted</th>
                    <th className="px-4 py-3 text-right">Lines</th>
                    <th className="px-4 py-3 text-left">Item(s) / Issued</th>
                    <th className="px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pageItems.length === 0 && (
                    <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-slate-400">No requisitions match your filters.</td></tr>
                  )}
                  {pageItems.map(r => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-semibold text-slate-800">{r.serialNo ?? `#${r.id}`}</td>
                      <td className="px-4 py-3 text-slate-600">{(r.type ?? r.requisitionType ?? "—")?.replace(/_/g, " ")}</td>
                      <td className="px-4 py-3 text-slate-700">{r.requesterName ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{r.departmentName ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{fmtDate(r.submittedAt)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-700">{r.lineCount ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[240px] truncate">
                        <ItemsCell lines={linesById[r.id]} pending={!linesById[r.id]} />
                      </td>
                      <td className="px-4 py-3">{statusBadge(r.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3 text-sm text-slate-500">
              <span>{filtered.length.toLocaleString()} record(s) — page {page + 1} of {totalPages}</span>
              <div className="flex items-center gap-2">
                <select
                  value={size}
                  onChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                  className="rounded-lg border px-2 py-1 text-sm"
                >
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  className="rounded-lg border px-3 py-1 disabled:opacity-40 hover:bg-slate-50">← Prev</button>
                <button onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}
                  className="rounded-lg border px-3 py-1 disabled:opacity-40 hover:bg-slate-50">Next →</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
